import { useState } from 'react';
import { formatDate } from '@iac/contracts';
import type { BrainNoteDto } from '@iac/contracts';
import { DataTable, Td, Tr } from '../../components/DataTable';
import { DeleteButton } from '../../components/DeleteButton';
import { IconPencilSquare, IconPlus } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { useDeleteNote, useNotes } from './api';
import { useLocale } from '../../lib/locale';
import { brainDict } from './i18n';
import { NoteDialog } from './NoteDialog';

/**
 * Everything that puts facts in, and everything that corrects them. The ask
 * screen can edit a note it cited, but adding and forgetting happen only here.
 */
export function BrainNotes() {
  const t = useDict(brainDict);
  usePageTitle(t.notesTitle);
  const { data: notes } = useNotes();
  const [dialog, setDialog] = useState<
    null | { mode: 'add' } | { mode: 'edit'; note: BrainNoteDto }
  >(null);

  return (
    <>
      <PageHeader
        title={t.notes}
        actions={
          <button
            type="button"
            className="btn-primary-icon"
            aria-label={t.rememberSomething}
            title={t.rememberSomething}
            onClick={() => setDialog({ mode: 'add' })}
          >
            <IconPlus />
          </button>
        }
      />

      <DataTable
        columns={[
          { label: t.note },
          { label: t.saved, align: 'center', priority: 'secondary' },
          { align: 'center' },
        ]}
        empty={t.noNotes}
      >
        {notes &&
          notes.length > 0 &&
          notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              onEdit={() => setDialog({ mode: 'edit', note })}
            />
          ))}
      </DataTable>

      <NoteDialog
        open={dialog !== null}
        note={dialog?.mode === 'edit' ? dialog.note : undefined}
        onClose={() => setDialog(null)}
      />
    </>
  );
}

/** Enough of a note to recognise it in a confirm dialog, which cannot scroll. */
const CONFIRM_PREVIEW = 80;

function NoteRow({ note, onEdit }: { note: BrainNoteDto; onEdit: () => void }) {
  const { locale } = useLocale();
  const t = useDict(brainDict);
  const remove = useDeleteNote();

  return (
    <Tr id={`note-row-${note.id}`}>
      <Td className="note-cell">{note.content}</Td>
      <Td className="text-hint">{formatDate(note.createdAt, locale)}</Td>
      <Td>
        <div className="row-actions">
          <button
            type="button"
            className="btn-icon-ghost"
            aria-label={t.editNote}
            title={t.editNote}
            onClick={onEdit}
          >
            <IconPencilSquare />
          </button>
          <DeleteButton
            label={t.deleteNote}
            confirm={t.confirmForget(
              note.content.length > CONFIRM_PREVIEW
                ? `${note.content.slice(0, CONFIRM_PREVIEW)}…`
                : note.content,
            )}
            onConfirm={() => remove.mutate(note.id)}
          />
        </div>
      </Td>
    </Tr>
  );
}
